import { useState, useCallback, useEffect, useRef } from "react";
import useAgentStore from "./useAgentStore";
import usePublicChatStore from "./usePublicChatStore";
import {
  STATUS_WAITING_FOR_REVIEW,
  parsePublicErrorMessage,
} from "./agentSessionsPanelUtils";

/**
 * Encapsulates all public-mode session state and logic:
 *  - public session list loading / polling
 *  - public session detail loading
 *  - public session token mapping
 *  - sending messages in public mode
 */
export default function usePublicSessions({
  isPublicMode,
  publicShareToken,
  publicIdentityPayload,
  showSidebar,
  initialPublicSessionToken,
  onSessionSelect,
  onPublicSessionTokenChange,
  onSessionInvalidated,
  onSetSelectedSessionId,
}) {
  const [selectedPublicSessionToken, setSelectedPublicSessionToken] = useState(
    initialPublicSessionToken || ""
  );
  const selectedPublicSessionTokenRef = useRef(initialPublicSessionToken || "");
  const [publicSessions, setPublicSessions] = useState([]);
  const [publicSessionsLoading, setPublicSessionsLoading] = useState(false);
  const [publicSessionDetail, setPublicSessionDetail] = useState(null);
  const [publicSessionDetailLoading, setPublicSessionDetailLoading] =
    useState(false);
  const [publicPendingUserMessage, setPublicPendingUserMessage] =
    useState(null);
  const [
    publicSessionTokenByChatSessionId,
    setPublicSessionTokenByChatSessionId,
  ] = useState({});
  const [publicError, setPublicError] = useState("");

  const notifyPublicSessionTokenChange = useCallback(
    (nextToken) => {
      if (!isPublicMode) return;
      if (onPublicSessionTokenChange) {
        onPublicSessionTokenChange(nextToken || "");
      }
    },
    [isPublicMode, onPublicSessionTokenChange]
  );

  const upsertPublicSessionTokenMapping = useCallback(
    (chatSessionId, publicSessionToken) => {
      if (!chatSessionId || !publicSessionToken) return;
      setPublicSessionTokenByChatSessionId((current) => {
        if (current[chatSessionId] === publicSessionToken) {
          return current;
        }
        return {
          ...current,
          [chatSessionId]: publicSessionToken,
        };
      });
    },
    []
  );

  const loadPublicSessionDetail = useCallback(
    async (publicSessionToken, { silent = false } = {}) => {
      if (!isPublicMode || !publicSessionToken) return null;
      if (!silent) {
        setPublicSessionDetailLoading(true);
      }
      try {
        const response = await usePublicChatStore
          .getState()
          .getPublicChatSession(
            publicSessionToken,
            publicIdentityPayload || {}
          );
        const nextSession = response?.data || null;
        setPublicSessionDetail((currentSession) => {
          if (!nextSession) return null;
          if (
            nextSession.status === STATUS_WAITING_FOR_REVIEW &&
            !nextSession.blockedWhileReviewMessage &&
            currentSession?.blockedWhileReviewMessage
          ) {
            return {
              ...nextSession,
              blockedWhileReviewMessage:
                currentSession.blockedWhileReviewMessage,
            };
          }
          return nextSession;
        });
        setPublicError("");
        if (nextSession?.chatSessionId) {
          if (onSetSelectedSessionId) {
            onSetSelectedSessionId(nextSession.chatSessionId);
          }
          upsertPublicSessionTokenMapping(
            nextSession.chatSessionId,
            publicSessionToken
          );
          if (onSessionSelect) {
            onSessionSelect(nextSession.chatSessionId);
          }
        }
        setPublicPendingUserMessage((currentPendingMessage) => {
          if (!currentPendingMessage || !Array.isArray(nextSession?.messages)) {
            return currentPendingMessage;
          }
          const hasPendingBeenPersisted = nextSession.messages.some(
            (chatMessage) =>
              chatMessage?.role === "user" &&
              String(chatMessage?.content || "") === currentPendingMessage
          );
          return hasPendingBeenPersisted ? null : currentPendingMessage;
        });
        return nextSession;
      } catch (error) {
        const errorMessage = parsePublicErrorMessage(
          error,
          "Unable to load this public chat session."
        );
        setPublicError(errorMessage);
        const status = error?.response?.status;
        if (status === 404 || status === 410 || status === 403) {
          setSelectedPublicSessionToken("");
          setPublicSessionDetail(null);
          setPublicPendingUserMessage(null);
          notifyPublicSessionTokenChange("");
          if (onSessionInvalidated) onSessionInvalidated();
        }
        return null;
      } finally {
        if (!silent) {
          setPublicSessionDetailLoading(false);
        }
      }
    },
    [
      isPublicMode,
      notifyPublicSessionTokenChange,
      onSessionSelect,
      onSessionInvalidated,
      onSetSelectedSessionId,
      publicIdentityPayload,
      upsertPublicSessionTokenMapping,
    ]
  );

  const loadPublicSessions = useCallback(
    async ({ silent = false } = {}) => {
      if (!isPublicMode || !publicShareToken || !showSidebar) return [];
      if (!silent) {
        setPublicSessionsLoading(true);
      }
      try {
        const response = await usePublicChatStore
          .getState()
          .listPublicChatSessions(
            publicShareToken,
            publicIdentityPayload || {}
          );
        const nextSessions = Array.isArray(response?.data)
          ? response.data
          : [];
        setPublicSessions(nextSessions);
        setPublicError("");
        setPublicSessionTokenByChatSessionId((current) => {
          const next = { ...current };
          nextSessions.forEach((session) => {
            if (session?.chatSessionId && session?.publicSessionToken) {
              next[session.chatSessionId] = session.publicSessionToken;
            }
          });
          return next;
        });
        const activePublicSessionToken =
          selectedPublicSessionTokenRef.current;
        if (activePublicSessionToken) {
          const matchingSession = nextSessions.find(
            (session) =>
              session?.publicSessionToken === activePublicSessionToken
          );
          if (matchingSession?.chatSessionId && onSetSelectedSessionId) {
            onSetSelectedSessionId(matchingSession.chatSessionId);
          }
        }
        return nextSessions;
      } catch (error) {
        setPublicSessions([]);
        setPublicError(
          parsePublicErrorMessage(
            error,
            "Unable to load public chat sessions."
          )
        );
        return [];
      } finally {
        if (!silent) {
          setPublicSessionsLoading(false);
        }
      }
    },
    [isPublicMode, publicShareToken, publicIdentityPayload, showSidebar, onSetSelectedSessionId]
  );

  // Keep ref in sync
  useEffect(() => {
    selectedPublicSessionTokenRef.current = selectedPublicSessionToken;
  }, [selectedPublicSessionToken]);

  // Load public sessions on mount / when deps change
  useEffect(() => {
    if (!isPublicMode || !publicShareToken || !showSidebar) return;
    loadPublicSessions();
  }, [isPublicMode, publicShareToken, loadPublicSessions, showSidebar]);

  // Poll public sessions
  useEffect(() => {
    if (!isPublicMode || !publicShareToken || !showSidebar) return;
    const id = setInterval(() => {
      loadPublicSessions({ silent: true });
    }, 10000);
    return () => clearInterval(id);
  }, [isPublicMode, publicShareToken, loadPublicSessions, showSidebar]);

  // Load initial public session detail
  useEffect(() => {
    if (!isPublicMode) return;
    const nextToken = initialPublicSessionToken || "";
    if (!nextToken) return;
    setSelectedPublicSessionToken(nextToken);
    loadPublicSessionDetail(nextToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPublicSessionToken, isPublicMode]);

  // Wire up public session change callback
  useEffect(() => {
    if (!isPublicMode || !showSidebar) return;
    const cb = () => loadPublicSessions({ silent: true });
    useAgentStore.getState().setPublicSessionChangeCallback(cb);
    return () => useAgentStore.getState().setPublicSessionChangeCallback(null);
  }, [isPublicMode, showSidebar, loadPublicSessions]);

  // Reset helpers used by session actions
  const resetPublicState = useCallback(() => {
    setSelectedPublicSessionToken("");
    setPublicSessionDetail(null);
    setPublicPendingUserMessage(null);
    useAgentStore.setState({
      sessionDetail: null,
      chatStatus: "ready",
      sendError: null,
    });
    notifyPublicSessionTokenChange("");
  }, [notifyPublicSessionTokenChange]);

  return {
    selectedPublicSessionToken,
    setSelectedPublicSessionToken,
    publicSessions,
    publicSessionsLoading,
    publicSessionDetail,
    publicSessionDetailLoading,
    publicPendingUserMessage,
    setPublicPendingUserMessage,
    publicSessionTokenByChatSessionId,
    publicError,
    setPublicError,
    notifyPublicSessionTokenChange,
    upsertPublicSessionTokenMapping,
    loadPublicSessionDetail,
    loadPublicSessions,
    resetPublicState,
  };
}
